// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * game.ts — the whole simulation. Pure, deterministic, DOM-free.
 *
 * A run is a pure function of (seed, mode, the ordered list of actions the
 * player took). That is not an accident of style, it is the netcode: Lastlight's
 * multiplayer is a parallel same-seed RACE, so every peer runs this class over
 * its OWN actions and nothing but the seed ever crosses the wire. There is
 * therefore no shared mutable state between peers and structurally nothing to
 * desync — see race.ts.
 *
 * The day is yours and the night is the game's. During the day you build, stoke
 * and dispatch; `endDay()` resolves the night in a fixed order and emits an
 * EVENT for everything it does. Those events are the game's account of itself,
 * and `tests/balance.test.ts` audits them from the outside at zero tolerance
 * (principle #21): a difficulty curve cannot tell you whether the mechanics
 * work, because a broken mechanic just makes runs end sooner and that is
 * indistinguishable from intended difficulty.
 */

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import {
  COST,
  FOOD_PER_PERSON,
  HUT_YIELD,
  SCOUT_NIGHTS,
  SHELTER_PER_HOME,
  WORKSHOP_BONUS,
  coldFor,
  type Mode,
} from './modes';
import { disc, fromOrigin, key, neighbours } from './hex';
import type { TerrainName } from './palette';

export type Resource = 'wood' | 'fish' | 'coal';
export type BuildingKind = 'home' | 'hut' | 'workshop';

/** People the generator itself keeps alive. Without this, night 1 is unsurvivable. */
export const GENERATOR_SHELTER = 2;

export interface Tile {
  q: number;
  r: number;
  terrain: TerrainName;
  /** Hidden until a scout reports, or until it started inside your vision. */
  fogged: boolean;
  building: BuildingKind | null;
  /** A cache waiting to be found. Cleared when a scout brings it home. */
  cache: CacheDrop | null;
}

export interface CacheDrop {
  kind: Resource | 'survivor';
  amount: number;
}

export interface Scout {
  q: number;
  r: number;
  /** Nights remaining before it is home. Delivers at 0. */
  nightsLeft: number;
}

/** What the terrain under a hut yields. Snow yields nothing, so no hut may sit on it. */
export const TERRAIN_YIELD: Partial<Record<TerrainName, Resource>> = {
  forest: 'wood',
  water: 'fish',
  seam: 'coal',
};

// ── the event stream ────────────────────────────────────────────────────────
// Deliberately RAW outcomes: what happened and how much, never "and this was
// correct". The audit in the balance sim derives the rule itself from the board
// and checks these against it, so it is a genuine external check rather than
// the simulation grading its own homework.

export type Ev =
  | { k: 'build'; kind: BuildingKind; q: number; r: number }
  | { k: 'stoke'; coal: number; heat: number }
  | { k: 'scoutOut'; q: number; r: number }
  | { k: 'scoutBack'; q: number; r: number; found: CacheDrop | null }
  | { k: 'labour'; workers: number; staffed: number; idle: number }
  | { k: 'produce'; q: number; r: number; res: Resource; amount: number }
  | { k: 'hutIdle'; q: number; r: number; reason: 'no-worker' | 'frozen' }
  | { k: 'feed'; ate: number; starved: number; fishLeft: number }
  | { k: 'shelter'; people: number; slots: number; froze: number; slotsFree: number }
  | { k: 'cold'; amount: number; heat: number }
  | { k: 'dawn'; day: number }
  | { k: 'end'; won: boolean; reason: string };

export interface GameOpts {
  seed: number;
  mode: Mode;
}

export interface Summary {
  score: number;
  survivors: number;
  buildings: number;
  caches: number;
  days: number;
  won: boolean;
  reason: string;
}

export class Game {
  readonly mode: Mode;
  readonly seed: number;
  readonly tiles = new Map<number, Tile>();

  /** 1-based. The day you are currently living. */
  day = 1;
  heat: number;
  pop: number;
  wood: number;
  fish: number;
  coal: number;
  scouts: Scout[] = [];
  /** Caches actually carried home. Score, and the reason to gamble a worker. */
  cachesBanked = 0;

  over = false;
  won = false;
  reason = '';

  /** Cleared by the renderer each frame; the sim never reads it back. */
  events: Ev[] = [];

  private readonly rng: Rng;

  /** Integer in [0, n). One place, so map generation stays reproducible. */
  private ri(n: number): number {
    return Math.floor(this.rng() * n);
  }

  /** Bookkeeping for the "no scout is ever lost" invariant. */
  scoutsSent = 0;
  scoutsReturned = 0;

  constructor(opts: GameOpts) {
    this.mode = opts.mode;
    this.seed = opts.seed;
    this.rng = makeRng(opts.seed);
    this.heat = opts.mode.startHeat;
    this.pop = opts.mode.startPop;
    this.wood = opts.mode.startWood;
    this.fish = opts.mode.startFish;
    this.coal = opts.mode.startCoal;
    this.generate();
  }

  // ── map generation ────────────────────────────────────────────────────────

  private generate(): void {
    const m = this.mode;
    const cells = disc(m.radius);

    for (const c of cells) {
      const d = fromOrigin(c.q, c.r);
      let terrain: TerrainName;
      if (d === 0) {
        // The generator's own tile. Never a resource — you cannot hut over it.
        terrain = 'snow';
      } else {
        const roll = this.rng();
        if (roll < 0.4) terrain = 'snow';
        else if (roll < 0.62) terrain = 'forest';
        else if (roll < 0.82) terrain = 'water';
        else terrain = 'seam';
      }
      this.tiles.set(key(c.q, c.r), {
        q: c.q,
        r: c.r,
        terrain,
        fogged: d > m.startVision,
        building: null,
        cache: null,
      });
    }

    this.guaranteeOpening();
    this.scatterCaches();
  }

  /**
   * Every start must be playable, and every peer's start must be the SAME kind
   * of playable. A purely random map can put all three resources out beyond the
   * opening warmth, which is not "a hard seed" — it is a run that cannot make
   * its first hut and dies to arithmetic. So each resource is guaranteed within
   * the OPENING RING, repaired deterministically from the same rng stream.
   *
   * The ring, specifically, and not the starting vision: the sim caught runs
   * dying on night 4 with zero huts built because on Longnight you can SEE
   * three tiles out but can only BUILD one tile out, so the water that fed you
   * was visible, known about, and unreachable. Every one of those deaths was
   * starvation with no counterplay available on night 1.
   */
  private guaranteeOpening(): void {
    const openingRing = Math.max(1, Math.min(Math.floor(this.mode.startHeat), this.mode.radius));
    const near = disc(openingRing).filter((c) => fromOrigin(c.q, c.r) > 0);
    for (const want of ['forest', 'water', 'seam'] as const) {
      const has = near.some((c) => this.tiles.get(key(c.q, c.r))?.terrain === want);
      if (has) continue;
      const spare = near.filter((c) => this.tiles.get(key(c.q, c.r))?.terrain === 'snow');
      const pool = spare.length ? spare : near;
      const pick = pool[this.ri(pool.length)];
      const t = this.tiles.get(key(pick.q, pick.r));
      if (t) t.terrain = want;
    }
  }

  private scatterCaches(): void {
    const m = this.mode;
    const far = disc(m.radius).filter((c) => fromOrigin(c.q, c.r) > m.startVision);
    if (!far.length) return;
    // Roughly a third of the fog is worth walking to. Fewer and scouting is a
    // sucker's bet; more and it stops being a gamble at all.
    const count = Math.max(2, Math.round(far.length * 0.33));
    const pool = [...far];
    for (let i = 0; i < count && pool.length; i++) {
      const pick = pool.splice(this.ri(pool.length), 1)[0];
      const t = this.tiles.get(key(pick.q, pick.r));
      if (!t) continue;
      const roll = this.rng();
      let drop: CacheDrop;
      if (roll < 0.22) {
        drop = { kind: 'survivor', amount: 1 };
      } else if (roll < 0.48) {
        drop = { kind: 'coal', amount: Math.round((4 + this.ri(4)) * m.cacheRichness) };
      } else if (roll < 0.76) {
        drop = { kind: 'wood', amount: Math.round((4 + this.ri(5)) * m.cacheRichness) };
      } else {
        drop = { kind: 'fish', amount: Math.round((4 + this.ri(5)) * m.cacheRichness) };
      }
      t.cache = drop;
    }
  }

  // ── queries ───────────────────────────────────────────────────────────────

  at(q: number, r: number): Tile | undefined {
    return this.tiles.get(key(q, r));
  }

  /** The warmth radius. Everything at or inside this distance is alive ground. */
  get warmth(): number {
    return Math.max(0, Math.floor(this.heat));
  }

  inWarmth(q: number, r: number): boolean {
    return fromOrigin(q, r) <= this.warmth;
  }

  /** Workers not currently walking around in the fog. */
  get workers(): number {
    return Math.max(0, this.pop - this.scouts.length);
  }

  /** Shelter slots that are actually keeping someone alive tonight. */
  get shelterSlots(): number {
    let slots = GENERATOR_SHELTER;
    for (const t of this.tiles.values()) {
      if (t.building === 'home' && this.inWarmth(t.q, t.r)) slots += SHELTER_PER_HOME;
    }
    return slots;
  }

  /** Workshops physically touching this tile. The buff is adjacency, nothing else. */
  workshopsAround(q: number, r: number): number {
    let n = 0;
    for (const nb of neighbours(q, r)) {
      const t = this.at(nb.q, nb.r);
      if (t?.building === 'workshop' && this.inWarmth(t.q, t.r)) n++;
    }
    return n;
  }

  buildings(): Tile[] {
    return [...this.tiles.values()].filter((t) => t.building !== null);
  }

  /** Huts that would produce tonight, in a stable staffing order. */
  liveHuts(): Tile[] {
    return [...this.tiles.values()]
      .filter((t) => t.building === 'hut')
      .sort((a, b) => {
        // Closest to the generator gets a worker first: deterministic, and it
        // matches what a player would do, so an unstaffed hut is always the
        // one furthest out rather than an arbitrary one.
        const da = fromOrigin(a.q, a.r);
        const db = fromOrigin(b.q, b.r);
        if (da !== db) return da - db;
        return key(a.q, a.r) - key(b.q, b.r);
      });
  }

  cost(kind: BuildingKind): number {
    return COST[kind];
  }

  /**
   * Why you cannot build here, or null if you can. One function so the UI's
   * grey-out, the keyboard path and the bot all agree on legality.
   */
  buildError(kind: BuildingKind, q: number, r: number): string | null {
    if (this.over) return 'The run is over.';
    const t = this.at(q, r);
    if (!t) return 'Off the map.';
    if (t.fogged) return 'Scout it first.';
    if (t.building) return 'Something is already here.';
    if (q === 0 && r === 0) return 'The generator stands here.';
    if (this.wood < COST[kind]) return `Needs ${COST[kind]} wood.`;
    if (kind === 'hut' && !TERRAIN_YIELD[t.terrain]) {
      return 'A hut must sit on forest, water or a coal seam.';
    }
    return null;
  }

  canBuild(kind: BuildingKind, q: number, r: number): boolean {
    return this.buildError(kind, q, r) === null;
  }

  // ── day actions ───────────────────────────────────────────────────────────

  build(kind: BuildingKind, q: number, r: number): boolean {
    if (!this.canBuild(kind, q, r)) return false;
    const t = this.at(q, r);
    if (!t) return false;
    t.building = kind;
    this.wood -= COST[kind];
    this.events.push({ k: 'build', kind, q, r });
    return true;
  }

  canStoke(): boolean {
    return !this.over && this.coal >= COST.stokeCoal;
  }

  stoke(): boolean {
    if (!this.canStoke()) return false;
    this.coal -= COST.stokeCoal;
    this.heat += COST.stokeHeat;
    this.events.push({ k: 'stoke', coal: COST.stokeCoal, heat: this.heat });
    return true;
  }

  scoutError(q: number, r: number): string | null {
    if (this.over) return 'The run is over.';
    const t = this.at(q, r);
    if (!t) return 'Off the map.';
    if (!t.fogged) return 'You have already seen this.';
    if (this.scouts.some((s) => s.q === q && s.r === r)) return 'A scout is already headed there.';
    if (this.workers <= 0) return 'Nobody left to send.';
    // Sending your last worker means nothing gets harvested tonight, which is
    // allowed and is exactly the gamble — but sending someone who does not
    // exist is not.
    return null;
  }

  canScout(q: number, r: number): boolean {
    return this.scoutError(q, r) === null;
  }

  sendScout(q: number, r: number): boolean {
    if (!this.canScout(q, r)) return false;
    this.scouts.push({ q, r, nightsLeft: SCOUT_NIGHTS });
    this.scoutsSent++;
    this.events.push({ k: 'scoutOut', q, r });
    return true;
  }

  // ── the night ─────────────────────────────────────────────────────────────

  /**
   * Resolve one night and advance to the next dawn. Fixed order, and the order
   * is load-bearing:
   *
   *   1. scouts   — a survivor arriving BEFORE the feed has to be fed tonight,
   *                 which is the honest reading of "they joined your people".
   *   2. produce  — a hut only pays if it is staffed AND inside the warmth.
   *   3. feed     — everyone eats; the shortfall starves.
   *   4. shelter  — whoever is left needs a slot inside the warmth.
   *   5. cold     — heat drops, so the ring you are judged by tomorrow is the
   *                 SMALLER one. Freezing people with tonight's ring and then
   *                 shrinking it would punish twice for one night.
   */
  endDay(): void {
    if (this.over) return;

    // Who is actually home to work tonight, counted before the scouts resolve:
    // a scout still in the fog at dusk spends the night walking, whether or not
    // they make it back by dawn, and a survivor carried home arrives too late
    // to have worked a shift.
    const onTheTools = this.workers;

    this.resolveScouts();
    this.resolveProduction(onTheTools);
    this.resolveFood();
    if (this.over) return;
    this.resolveShelter();
    if (this.over) return;

    // The bill is for the ring you actually held tonight, before it shrinks.
    const bite = coldFor(this.mode, this.day, this.warmth);
    this.heat -= bite;
    this.events.push({ k: 'cold', amount: bite, heat: this.heat });
    if (this.heat <= 0) {
      this.heat = 0;
      return this.finish(false, 'The generator went dark.');
    }

    if (this.day >= this.mode.days) {
      return this.finish(true, 'You held out.');
    }

    this.day++;
    this.events.push({ k: 'dawn', day: this.day });
  }

  private resolveScouts(): void {
    const still: Scout[] = [];
    for (const s of this.scouts) {
      s.nightsLeft--;
      if (s.nightsLeft > 0) {
        // Still walking. (SCOUT_NIGHTS is 1, so nothing reaches this today —
        // it is kept because the trip length is a tuning constant, not a rule,
        // and a longer trip must not silently lose its scouts.)
        still.push(s);
        continue;
      }
      // Home.
      this.reveal(s.q, s.r, 1);
      const t = this.at(s.q, s.r);
      const found = t?.cache ?? null;
      if (t && found) {
        t.cache = null;
        this.cachesBanked++;
        if (found.kind === 'survivor') this.pop += found.amount;
        else if (found.kind === 'wood') this.wood += found.amount;
        else if (found.kind === 'fish') this.fish += found.amount;
        else this.coal += found.amount;
      }
      this.scoutsReturned++;
      this.events.push({ k: 'scoutBack', q: s.q, r: s.r, found });
    }
    this.scouts = still;
  }

  private reveal(q: number, r: number, spread: number): void {
    const t = this.at(q, r);
    if (t) t.fogged = false;
    if (spread <= 0) return;
    for (const nb of neighbours(q, r)) {
      const n = this.at(nb.q, nb.r);
      if (n) n.fogged = false;
    }
  }

  private resolveProduction(onTheTools: number): void {
    const huts = this.liveHuts();
    let free = onTheTools;
    let staffed = 0;

    for (const t of huts) {
      if (!this.inWarmth(t.q, t.r)) {
        // Outside the ring it is simply dead, regardless of who is available.
        this.events.push({ k: 'hutIdle', q: t.q, r: t.r, reason: 'frozen' });
        continue;
      }
      if (free <= 0) {
        this.events.push({ k: 'hutIdle', q: t.q, r: t.r, reason: 'no-worker' });
        continue;
      }
      free--;
      staffed++;
      const res = TERRAIN_YIELD[t.terrain];
      if (!res) continue;
      const amount = HUT_YIELD + WORKSHOP_BONUS * this.workshopsAround(t.q, t.r);
      if (res === 'wood') this.wood += amount;
      else if (res === 'fish') this.fish += amount;
      else this.coal += amount;
      this.events.push({ k: 'produce', q: t.q, r: t.r, res, amount });
    }

    this.events.push({ k: 'labour', workers: onTheTools, staffed, idle: free });
  }

  private resolveFood(): void {
    const need = this.pop * FOOD_PER_PERSON;
    const ate = Math.min(need, this.fish);
    this.fish -= ate;
    // One person starves per unit of shortfall.
    const starved = Math.min(this.pop, need - ate);
    this.pop -= starved;
    this.events.push({ k: 'feed', ate, starved, fishLeft: this.fish });
    if (this.pop <= 0) this.finish(false, 'Your people starved.');
  }

  private resolveShelter(): void {
    const slots = this.shelterSlots;
    const froze = Math.max(0, this.pop - slots);
    // Scouts are out in the cold by definition, but they carry a stove and are
    // not counted against shelter — otherwise scouting would be a death
    // sentence rather than a gamble, and nobody would ever use the mechanic.
    this.pop -= froze;
    this.events.push({
      k: 'shelter',
      people: this.pop + froze,
      slots,
      froze,
      slotsFree: Math.max(0, slots - (this.pop + froze)),
    });
    if (this.pop <= 0) this.finish(false, 'The cold took everyone.');
  }

  private finish(won: boolean, reason: string): void {
    if (this.over) return;
    this.over = true;
    this.won = won;
    this.reason = reason;
    this.events.push({ k: 'end', won, reason });
  }

  // ── result ────────────────────────────────────────────────────────────────

  summary(): Summary {
    const buildings = this.buildings().length;
    const days = this.won ? this.mode.days : this.day - 1;
    const score = this.over
      ? this.pop * 10 + buildings * 4 + this.cachesBanked * 3 + days * 5
      : 0;
    return {
      score: Math.max(0, score),
      survivors: this.pop,
      buildings,
      caches: this.cachesBanked,
      days: Math.max(0, days),
      won: this.won,
      reason: this.reason,
    };
  }

  /** Live score, for the standings strip while a race is running. */
  liveScore(): number {
    const days = this.won ? this.mode.days : Math.max(0, this.day - 1);
    return Math.max(0, this.pop * 10 + this.buildings().length * 4 + this.cachesBanked * 3 + days * 5);
  }
}

/** The worldwide daily seed. Same map for everyone, everywhere, all day. */
export function dailySeed(now = new Date()): number {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  const d = now.getUTCDate();
  let h = 2166136261;
  for (const ch of `lastlight-${y}-${m}-${d}`) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
